import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { subject } from '@casl/ability';
import history from 'helpers/history';
import { Button, BUTTON_TYPES, Card } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { PopupAssignRecruiters } from '_molecules';
import { ReactComponent as Image } from 'dist/emptyStates/dashboardOpening.svg';
import { ACTIONS, Can, ROLES, SUBJECTS, UI } from 'permission';
import { JOB_STATUSES } from 'constants/job';
import { assignHrToJobs } from 'actions';
import { useAppDispatch } from 'store';
import './style.scss';
import { getUser } from 'store/selectors/authorizeSelectors';

interface StepsEmptyTeamMember {
  uuid?: string;
}

interface StepsEmptyProps {
  showPopupUnarchive: (v: boolean) => void;
  jobStatus?: string;
  recruiters: StepsEmptyTeamMember[];
  recruiterLeads: StepsEmptyTeamMember[];
}

export const StepsEmpty: React.FC<StepsEmptyProps> = ({
  showPopupUnarchive,
  jobStatus,
  recruiters,
  recruiterLeads,
}) => {
  const { t } = useTranslation();
  const { id: jobId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const user = useSelector(getUser);

  const [openAssign, setOpenAssign] = useState(false);

  const onOpenAssignPopup = () => setOpenAssign(true);
  const onCloseAssignPopup = () => setOpenAssign(false);
  const onSaveRecruiters = (list: string[], asRecruiters: string[]) => {
    dispatch(assignHrToJobs(jobId, list, asRecruiters)).then(() => {
      setOpenAssign(false);
    });
  };

  const onCreateOpening = () => history.push(`/jobs/${jobId}/create-opening`);
  const onUnarchive = () => showPopupUnarchive(true);

  const isDraft = jobStatus === JOB_STATUSES.DRAFT;
  const isArchived = jobStatus === JOB_STATUSES.ARCHIVED;

  const accessToJob =
    user.active_role.name === ROLES.RECRUITER_LEAD ? recruiterLeads.some(r => r.uuid === user.uuid) : true;

  if (isArchived) {
    return (
      <Card className="steps-empty">
        <Image className="steps-empty__image" />
        <Can I={ACTIONS.READ} a={UI.ARCHIVE_JOB} passThrough>
          {access => (
            <>
              <Typography.Title3 className="steps-empty__title" weight={TYPOGRAPHY_WEIGHT.BOLD}>
                {access
                  ? t('translation:steps_empty.title_unarchive_required')
                  : t('translation:steps_empty.title_no_steps_available')}
              </Typography.Title3>
              {access && (
                <Button className="steps-empty__button" type={BUTTON_TYPES.PRIMARY} onClick={onUnarchive}>
                  {t('translation:steps_empty.button_unarchive_job')}
                </Button>
              )}
            </>
          )}
        </Can>
      </Card>
    );
  }

  if (isDraft) {
    return (
      <Card className="steps-empty">
        <Image className="steps-empty__image" />
        <Can I={ACTIONS.CREATE} a={SUBJECTS.OPENING} passThrough>
          {access => (
            <>
              <Typography.Title3 className="steps-empty__title" weight={TYPOGRAPHY_WEIGHT.BOLD}>
                {access
                  ? t('translation:steps_empty.title_create_opening')
                  : t('translation:steps_empty.title_no_steps_available')}
              </Typography.Title3>
              <Typography.Text className="steps-empty__text">
                {t('translation:steps_empty.text_steps_will_be_displayed')}
              </Typography.Text>
              {access && (
                <Button className="steps-empty__button" type={BUTTON_TYPES.PRIMARY} onClick={onCreateOpening}>
                  {t('translation:steps_empty.button_create_opening')}
                </Button>
              )}
            </>
          )}
        </Can>
      </Card>
    );
  }

  return (
    <Card className="steps-empty">
      <Image className="steps-empty__image" />
      <Typography.Title3 className="steps-empty__title" weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {t('translation:steps_empty.title_no_steps_available')}
      </Typography.Title3>
      <Typography.Text className="steps-empty__text">
        {t('translation:steps_empty.text_steps_will_be_displayed')}
      </Typography.Text>
      <Can I={ACTIONS.READ} this={subject(UI.ASSIGN_RECRUITER, { status: jobStatus })} passThrough>
        {access =>
          access &&
          accessToJob && (
            <Button className="steps-empty__button" type={BUTTON_TYPES.PRIMARY} onClick={onOpenAssignPopup}>
              {t('translation:steps_empty.button_assign_recruiters')}
            </Button>
          )
        }
      </Can>
      <PopupAssignRecruiters
        open={openAssign}
        onClose={onCloseAssignPopup}
        onSave={onSaveRecruiters}
        currentRecruiters={[...recruiterLeads, ...recruiters].map(r => ({ value: r.uuid }))}
      />
    </Card>
  );
};
